import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  DownOutlined,
  CloseOutlined,
  EllipsisOutlined,
  QuestionCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Tree, Input, Modal, Row, Col, Button, Tooltip, Space, message } from 'antd';
import type { DataNode, EventDataNode } from 'antd/es/tree';
import { ReactSortable } from 'react-sortablejs';

import CreateFromExistingMetricModal from './CreateFromExistingMetricModal';
import { deepClone } from '../../../../helpers/utils/deepClone';
import EditMetricsModal from './EditMetricsModal';
import { ADS_KEYS as ads_keys, ADS_METRICS_KEYS as ads_metrics_keys, REGISTRATION_GROUPS_NODE_NAME } from './modal-settings/config';
import MetricItem from './MetricItem';
import GroupItem from './GroupItem';
import CreateGroupModal from './group-modals/CreateGroupModal';
import EditGroupModal from './group-modals/EditGroupModal';
import {
  findObjectById,
  getLineHeightForModalSettings,
} from '../../../../helpers/utils/functions';
import { getAdsDimConflictMessage, hasAdsDimConflict, isAdsDimKey } from '../../../../helpers/utils/adsDims';
import {
  filterGroupsDisabledData,
  getChildrenKey,
  loopSearch,
  getMetricsParentKey,
  prepareTreeData,
  PERSONAL_DATA_KEYS,
} from '../../../../helpers/utils/reports';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../../../helpers/utils/ui';
import { resolveUtmFilterId } from '../../../../helpers/utils/utmSections';
import { unApi } from '../../api/endpoints/reports/unApi';
import { groupingsData2 } from '../../constants/reportsAD';
import { useActions } from '../../hooks/useActions';
import { useForceUpdate } from '../../hooks/useForceUpdate';
import { useLoadGroupingsData } from '../../hooks/useLoadGroupingsData';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { getCookie } from '../../../../helpers/utils/cookies';
import { deleteCustomMetricWithConfirm } from '../../../../helpers/utils/metricsDelete';
import { deleteCustomGroupWithConfirm } from '../../../../helpers/utils/groupsDelete';
import { purgeDeletedMetricsFromRedux } from '../../../../helpers/utils/deletedMetrics';

// ==================== Типы ====================

interface TreeNodeData extends DataNode {
  dataKey?: string;
  name?: string;
  label?: string;
  description?: string;
  background?: string;
  id?: number;
  children?: TreeNodeData[];
  disabled?: boolean;
  disableCheckbox?: boolean;
  checkable?: boolean;
  selectable?: boolean;
  /** Узел «Параметры Яндекс.Директ» (см. `injectUtmSectionsNode`) */
  isUtmSectionNode?: boolean;
  /** Почему срез Директа сейчас нельзя отметить (см. `getAdsDimConflictMessage`) */
  adsDimConflict?: string;
}

interface SelectedItem {
  key?: string;
  dataKey?: string;
  name?: string;
  label?: string;
  background?: string;
  id?: number | string;
  [key: string]: unknown;
}

interface ListKeyItem {
  key: string;
  dataKey?: string;
  name: string;
  [key: string]: unknown;
}

// Признак выбранной группы регистрации, требующей визуального схлопывания в списке
// выбранных осей. В новой структуре схлопывать НЕЧЕГО: переключатель набора
// (custom_group_set) — уже один элемент, а отдельные группы (custom_group_<id>) —
// обычные оси. Поэтому спец-обработки нет: все элементы рендерятся как обычные чипы.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isRegistrationGroupItem = (_item?: SelectedItem): boolean => false;

/**
 * Стягивает все выбранные группы регистрации в один смежный блок на позиции
 * ПЕРВОЙ такой группы, сохраняя порядок обычных осей и порядок групп между собой.
 * Нужно, чтобы в списке выбранных группировок группы отображались единым блоком
 * «Группы регистраций» (визуальное схлопывание), как и уходят в запрос.
 */
const collapseGroupsAdjacent = (list: SelectedItem[]): SelectedItem[] => {
  if (!Array.isArray(list)) return [];
  if (list.findIndex(isRegistrationGroupItem) === -1) return list;
  const groups = list.filter(isRegistrationGroupItem);
  const result: SelectedItem[] = [];
  let inserted = false;
  list.forEach((item) => {
    if (isRegistrationGroupItem(item)) {
      if (!inserted) {
        result.push(...groups);
        inserted = true;
      }
      // остальные группы уже добавлены единым блоком — пропускаем
    } else {
      result.push(item);
    }
  });
  return result;
};

/**
 * Схлопывание групп после перетаскивания с учётом позиции сброса (drop).
 * Группы всегда остаются единым смежным блоком, но:
 *  - если тащили ГРУППУ — блок целиком встаёт на место сброса (можно двигать блок
 *    вверх/вниз относительно других осей и менять порядок групп между собой);
 *  - если тащили обычную ось (или индекс неизвестен) — блок остаётся на позиции
 *    первой группы (прежнее поведение `collapseGroupsAdjacent`).
 * @param list    новый порядок из ReactSortable
 * @param newIndex индекс перетащенного элемента (evt.newIndex), либо -1 если неизвестно
 */
const collapseGroupsAfterDrag = (list: SelectedItem[], newIndex: number): SelectedItem[] => {
  if (!Array.isArray(list)) return [];
  const groups = list.filter(isRegistrationGroupItem);
  if (groups.length === 0) return list;

  const draggedIsGroup =
    newIndex >= 0 && newIndex < list.length && isRegistrationGroupItem(list[newIndex]);

  // Обычную ось тащили (или индекс неизвестен) — блок групп не перемещаем
  if (!draggedIsGroup) {
    return collapseGroupsAdjacent(list);
  }

  // Тащили группу — весь блок встаёт на место сброса.
  // Считаем, сколько обычных осей стоит перед точкой сброса — туда и вставляем блок.
  let nonGroupsBefore = 0;
  for (let i = 0; i < newIndex; i += 1) {
    if (!isRegistrationGroupItem(list[i])) nonGroupsBefore += 1;
  }

  const nonGroups = list.filter((item) => !isRegistrationGroupItem(item));
  const result: SelectedItem[] = [];
  let inserted = false;
  nonGroups.forEach((item, idx) => {
    if (idx === nonGroupsBefore && !inserted) {
      result.push(...groups);
      inserted = true;
    }
    result.push(item);
  });
  if (!inserted) result.push(...groups);
  return result;
};

interface ModalSettingsProps {
  title: 'Группировки' | 'Метрики';
  dataItems: TreeNodeData[];
  listKeys: ListKeyItem[];
  defSelected: SelectedItem[];
  defChecked: React.Key[];
  actionSet: (selected: SelectedItem[]) => void;
  isDisable?: boolean;
  hidePersonalDataGroups?: boolean;
  adsGroupsAvailable?: boolean;
  adsGroupsSelected?: boolean;
  setAdsGroupsAvailable?: (value: boolean) => void;
  setAdsGroupsSelected?: (value: boolean) => void;
  isAdReport?: boolean;
  /**
   * Управляемое открытие модалки (для дашбордов/вложенных форм).
   * Если не задано — используется внутренний стейт и показывается кнопка-триггер.
   */
  isVisible?: boolean;
  setVisible?: (value: boolean) => void;
  hideTriggerButton?: boolean;
  /**
   * Ограничение выбора одним элементом (например, number-блоки).
   */
  singleSelect?: boolean;
  // Новые опциональные флаги для унификации
  enableCustomMetrics?: boolean;  // default: true для Метрик — показывает кнопку "Создать метрику" и MetricItem
  enableLazyLoading?: boolean;    // default: true — поддержка loadData для динамической загрузки
  simplified?: boolean;           // default: false — упрощенный режим без tooltips и MetricItem
  modalWidth?: string | number;   // default: '60%' — ширина модального окна
  extraFieldSuffix?: string;      // суффикс для dataKey доп. полей (например '_2' для отчёта по заказам)
}

function ClearOutlined() {
  return null;
}

// AD-отчёт: пока не выбрана группировка по рекламе, все не-«Реклама» узлы верхнего
// уровня недоступны (первым уровнем обязана быть реклама). Применяется как ПРОИЗВОДНАЯ
// для рендера дерева, чтобы не зависеть от порядка эффектов setData (несколько эффектов
// сбрасывают data из dataItems и перетирали init-блокировку — баг с ~02.2026).
const AD_ROOT_NAME = 'Реклама';
const blockNode = (node: TreeNodeData): TreeNodeData => ({
  ...node, checkable: false, disableCheckbox: false, children: [], disabled: true,
});
// «Параметры Яндекс.Директ» внутри «Рекламы» доступны первым уровнем наравне с остальными
// рекламными осями (см. `isAdGroupingKey`), поэтому ветку «Рекламы» не трогаем целиком.
const blockNonAdGroupings = (tree: TreeNodeData[]): TreeNodeData[] =>
  (Array.isArray(tree) ? tree : []).map((node) => (node?.name !== AD_ROOT_NAME ? blockNode(node) : node));

/**
 * Срез «Площадка» в «Параметрах Яндекс.Директ» не сочетается с остальными срезами Директа
 * (отдельная таблица на бэке): пока выбран один — чекбокс другого недоступен, а по клику
 * показываем причину. Производная от выбора, как и `blockNonAdGroupings`: пересчитывается
 * и при снятии галочки в дереве, и при удалении крестиком в списке справа.
 * @param tree дерево группировок
 * @param selectedKeys ключи выбранных группировок (ключ листа = dataKey)
 */
const applyAdsDimConflicts = (tree: TreeNodeData[], selectedKeys: string[]): TreeNodeData[] =>
  (Array.isArray(tree) ? tree : []).map((node) => {
    if (Array.isArray(node?.children) && node.children.length > 0) {
      return { ...node, children: applyAdsDimConflicts(node.children, selectedKeys) };
    }
    const conflict = getAdsDimConflictMessage(String(node?.dataKey ?? node?.key ?? ''), selectedKeys);
    return conflict ? { ...node, disableCheckbox: true, adsDimConflict: conflict } : node;
  });

/**
 * Рекламная ли ось для правила AD-отчёта «первым уровнем — реклама».
 * Рекламные — оси из ads_keys и пункты «Параметров Яндекс.Директ»: срезы Директа (`ads_dim_*`)
 * и параметры меток (`utm_param_*` / `utm_cabinet_*`); разбор ключа структурный, как на бэке.
 * @param key dataKey или ключ узла дерева (ключ = dataKey)
 */
const isAdGroupingKey = (key: string): boolean =>
  ads_keys.includes(key) || isAdsDimKey(key) || resolveUtmFilterId(key) !== undefined;

const ModalSettings: FC<ModalSettingsProps> = ({
  title,
  dataItems,
  listKeys,
  defSelected,
  defChecked,
  actionSet,
  isDisable,
  hidePersonalDataGroups = false,
  adsGroupsAvailable,
  adsGroupsSelected,
  setAdsGroupsAvailable,
  setAdsGroupsSelected,
  isAdReport = false,
  isVisible,
  setVisible,
  hideTriggerButton = false,
  singleSelect = false,
  enableCustomMetrics = true,
  enableLazyLoading = true,
  simplified = false,
  modalWidth = '60%',
  extraFieldSuffix,
}) => {
  const forceUpdate = useForceUpdate();

  const {
    setMetricsFiltersUpdate,
    setIsNotParamsFilterVisible,
    getCreatedMetrics,
    setIsCreateMetricsVisible,
    setSkipTableFetchUntil,
    getCustomGroups,
    setGroupings: setGroupingsAction,
  } = useActions();

  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
  const { isCreateMetricsVisible } = useTypedSelector((state) => state.reports);
  const { groupings: rawGroupings, metrics: rawMetrics, metrics_filters_update, createdMetrics } =
    useTypedSelector((state) => state.table);
  
  // Фильтруем undefined элементы для защиты от ошибок при работе с блоками дашборда
  // Используем useMemo чтобы избежать создания нового массива при каждом рендере
  const groupings = useMemo(() => (rawGroupings ?? []).filter(Boolean), [rawGroupings]);
  const metrics = useMemo(() => (rawMetrics ?? []).filter(Boolean), [rawMetrics]);

  const [ creatingMetricState, setCreatingMetricState ] = useState<{
    visible: boolean;
    id: null;
  }>({
    visible: false,
    id: null,
  });
  const { filters } = useTypedSelector((state) => state.filter);
  const [ isConverse, setIsConverse ] = useState(false);
  const [ editMetricsState, setEditMetricsState ] = useState<{
    visible: boolean;
    id: null | number;
    type: string | null;
  }>({
    visible: false,
    id: null,
    type: null,
  });

  // Состояния модалок «Групп регистраций»
  const [ createGroupState, setCreateGroupState ] = useState<{
    visible: boolean;
    initialData: any;
    duplicate: boolean;
  }>({
    visible: false,
    initialData: null,
    duplicate: false,
  });
  const [ editGroupState, setEditGroupState ] = useState<{
    visible: boolean;
    id: number | null;
  }>({
    visible: false,
    id: null,
  });

  const [ isModalVisibleState, setIsModalVisibleState ] = useState(false);
  const isModalVisible = typeof isVisible === 'boolean' ? isVisible : isModalVisibleState;
  const setIsModalVisible = useCallback((value: boolean) => {
    if (setVisible) {
      setVisible(value);
      return;
    }
    setIsModalVisibleState(value);
  }, [setVisible]);
  const [ data, setData ] = useState<TreeNodeData[]>(dataItems);
  const [ selected, setSelected ] = useState<SelectedItem[]>(defSelected);
  // Список выбранных осей с визуально схлопнутыми (смежными) группами регистрации.
  const collapsedSelected = useMemo(() => collapseGroupsAdjacent(selected), [selected]);
  // Индекс сброса последнего перетаскивания (evt.newIndex из onUpdate) — нужен, чтобы
  // при перетаскивании ГРУППЫ переместить весь блок групп на место сброса.
  const dragNewIndexRef = useRef<number>(-1);
  const [ checkedKeys, setCheckedKeys ] = useState<React.Key[]>(defChecked);
  const [ expandedKeys, setExpandedKeys ] = useState<React.Key[]>([]);
  const [ searchValue, setSearchValue ] = useState('');
  const [ autoExpandParent, setAutoExpandParent ] = useState(true);
  const [ adsData, setAdsData ] = useState<TreeNodeData[]>([]);
  const [ notAdsData, setNotAdsData ] = useState<TreeNodeData[][]>([]);
  const { role: userRole, isContact } = useTypedSelector((state) => state.currentUser);
  const [ isDisabledSelected, setisDisabledSelected ] = useState(false);
  const [ notAdGroupings, setNotAdsGroupings ] = useState(false);

  const { onLoadData } = useLoadGroupingsData(setData, undefined, undefined, extraFieldSuffix);
  
  // Для AD отчёта: отслеживание состояния рекламных группировок
  const isAd = useRef(0);

  // Проверка, является ли элемент рекламной группировкой
  const isAdGrouping = useCallback((item: SelectedItem | TreeNodeData | null | undefined): boolean => {
    // Проверяем по dataKey
    if (item?.dataKey && isAdGroupingKey(item.dataKey)) {
      return true;
    }
    // Проверяем по ключу (ключ = dataKey)
    return isAdGroupingKey(String(item?.key ?? ''));
  }, []);

  // AD-отчёт: первым уровнем обязана быть группировка по рекламе. Нормализует
  // список выбранных: если первой оказалась не-рекламная (например, после удаления
  // первой рекламной), поднимаем рекламную наверх; если рекламных не осталось вовсе —
  // прочие оси недопустимы без рекламы → очищаем выбор (возврат к «сначала реклама»).
  const enforceAdFirst = useCallback((list: SelectedItem[]): SelectedItem[] => {
    if (!(isAdReport && title === 'Группировки')) return list;
    if (list.length === 0 || isAdGrouping(list[0])) return list;
    const adIndex = list.findIndex((item) => isAdGrouping(item));
    if (adIndex > 0) {
      const next = [ ...list ];
      const [ adItem ] = next.splice(adIndex, 1);
      next.unshift(adItem);
      return next;
    }
    return [];
  }, [ isAdReport, title, isAdGrouping ]);

  // При открытии модального окна синхронизируем checkedKeys с defChecked
  const prevIsModalVisibleRef = useRef(false);
  const isModalVisibleRef = useRef(isModalVisible);
  
  // Обновляем ref при изменении isModalVisible
  useEffect(() => {
    isModalVisibleRef.current = isModalVisible;
  }, [isModalVisible]);
  
  // Синхронизируем состояние при открытии модального окна
  useEffect(() => {
    const wasModalClosed = !prevIsModalVisibleRef.current;
    const isModalOpening = isModalVisible && wasModalClosed;
    
    if (isModalOpening) {
      // Модальное окно только что открылось — синхронизируем состояние из props
      // Нормализация key → dataKey только для Метрик: фикс старых отчётов с позиционными ключами '0-0-1'
      if (title === 'Метрики') {
        setSelected(defSelected.map((item: any) => ({ ...item, key: item.dataKey || item.key })));
      } else {
        setSelected([...defSelected]);
      }
      setCheckedKeys([...defChecked]);
    }
    
    prevIsModalVisibleRef.current = isModalVisible;
  }, [isModalVisible, defSelected, defChecked]);


  useEffect(() => {
    if (isCreateMetricsVisible) {
      // При открытии устанавливаем сразу
      setIsNotParamsFilterVisible(true);
    } else {
      // При закрытии делаем задержку, чтобы дать время cleanup'у CreateMetricsModal завершиться
      // и избежать перезагрузки отчёта при восстановлении фильтров
      setTimeout(() => setIsNotParamsFilterVisible(false), 300);
    }
  }, [ isCreateMetricsVisible ]);

  useEffect(() => {
    if (editMetricsState.visible) {
      // При открытии устанавливаем сразу
      setIsNotParamsFilterVisible(true);
    } else {
      // При закрытии делаем задержку, чтобы дать время cleanup'у EditMetricsModal завершиться
      // Перезагрузка отчёта при save_visual предотвращается через skipTableFetchUntil в metaSlice
      setTimeout(() => setIsNotParamsFilterVisible(false), 300);
    }
  }, [ editMetricsState.visible ]);

  useEffect(() => {
    setTimeout(() => {
      if (metrics_filters_update) {
        if (title === 'Группировки') {
          let _gr = groupings.filter(Boolean).map((item) => {
            if (!item?.dataKey) return item;
            let _key = getChildrenKey(item.dataKey, data);

            if (_key) {
              return {
                ...item,
                key: _key,
              };
            } else {
              return item;
            }
          }).filter(Boolean);

          setSelected(_gr);
          setCheckedKeys(_gr.map((item) => item?.key).filter(Boolean));

          _gr.forEach((item) => {
            if (item?.dataKey && ads_keys.includes(item.dataKey)) {
              setAdsGroupsSelected?.(true);
              setAdsGroupsAvailable?.(true);
              return;
            }
          });
        } else if (title === 'Метрики') {
          // Нормализуем key к dataKey: ключи в дереве = dataKey,
          // позиционные пути ('0-0-1') ломают galочки в старых сохранённых отчётах
          let _mr = metrics.filter(Boolean).map((item) => {
            if (!item?.dataKey) return item;
            return { ...item, key: item.dataKey };
          }).filter(Boolean);
          setSelected(_mr);
          setCheckedKeys(_mr.map((item: any) => item?.dataKey).filter(Boolean));
        }

        setMetricsFiltersUpdate(false);
      }
    }, 100);
  }, [ metrics_filters_update ]);

  useEffect(() => {
    if (title === 'Группировки' || title === 'Метрики') {
      const newNotAdsData: any[][] = [];
      dataItems.forEach((item, index) => {
        if (item.name !== 'Реклама') {
          newNotAdsData[index] = item.children || [];
        }
      });
      setNotAdsData(newNotAdsData);
    }
  }, [dataItems, title]);

  // Инициализация для AD отчёта при первом рендере
  useEffect(() => {
    if (isAdReport) {
      if (title === 'Группировки') {
        // Проверяем, есть ли уже выбранные рекламные группировки
        const hasAdGroupingSelected = selected.some((el) => isAdGrouping(el));
        
        if (hasAdGroupingSelected) {
          // Уже есть рекламная группировка — разблокируем всё
          isAd.current = 1;
          setData(prepareTreeData(JSON.parse(JSON.stringify(groupingsData2))));
          setNotAdsGroupings(true);
        } else {
          // Блокируем все не-рекламные группировки
          setData((prev) =>
            prev.map((el) => {
              if (el.name !== 'Реклама') {
                return {
                  ...el,
                  checkable: false,
                  disableCheckbox: false,
                  children: [],
                  disabled: true,
                };
              }
              return el;
            })
          );
          isAd.current = 2;
          setNotAdsGroupings(false);
        }
        forceUpdate();
      } else if (title === 'Метрики') {
        // Для AD отчёта метрики рекламы всегда доступны
        setData((prev) =>
          prev.map((el) => {
            if (el.name === 'Реклама') {
              return {
                ...el,
                disabled: false,
                selectable: true,
              };
            }
            return el;
          })
        );
        forceUpdate();
      }
    }
  }, [isAdReport]);

  // Обработка изменений selected для группировок
  // Используем useRef чтобы избежать бесконечного цикла
  const prevSelectedLengthRef = useRef(selected.length);
  
  useEffect(() => {
    if (title !== 'Группировки') return;
    
    // Стандартная логика для не-AD отчётов
    if (!isAdReport) {
      if (selected.length > 0) {
        if (!data[0]?.children && dataItems[0]?.children) {
          setData(dataItems);
        }
        const _keys = ads_keys;
        let hasAds = false;
        selected.filter(Boolean).forEach((item) => {
          if (item?.dataKey && _keys.includes(item.dataKey)) {
            hasAds = true;
          }
        });
        setAdsGroupsSelected?.(hasAds);
        setAdsGroupsAvailable?.(true);
      }
    }
    
    prevSelectedLengthRef.current = selected.length;
  }, [ selected.length, dataItems, isAdReport, title ]);

  useEffect(() => {
    if (title === 'Метрики') {
      setData((prevData) => {
        const _data = [...prevData];

        // Для AD отчёта метрики рекламы всегда доступны
        if (isAdReport || adsGroupsSelected) {
          _data.forEach((item) => {
            if (item.name === 'Реклама') {
              item.disabled = false;
              item.selectable = true;
              if (adsData.length > 0) {
                item.children = adsData;
              }
            }
          });
        } else {
          _data.forEach((item) => {
            if (item.name === 'Реклама') {
              item.disabled = true;
              item.selectable = false;
              if (item.children && item.children.length > 0) {
                setAdsData(item.children);
              }
              item.children = [];
            }
          });
        }

        return _data;
      });
    }
  }, [ adsGroupsSelected, isAdReport ]);

  useEffect(() => {
    if (title === 'Группировки') {
      setData((prevData) => {
        const _data = [...prevData];

        // Реклама всегда доступна — не блокируем
        _data.forEach((item) => {
          if (item.name === 'Реклама') {
            item.disabled = false;
            item.selectable = true;
            if (adsData.length > 0) {
              item.children = adsData;
            }
          }
        });

        return _data;
      });
    }
  }, [ adsGroupsAvailable ]);

  const shouldHidePersonalData = hidePersonalDataGroups || (userRole === 'employee' && !isContact);

  useEffect(() => {
    if (shouldHidePersonalData) {
      setData(
        filterGroupsDisabledData(dataItems, PERSONAL_DATA_KEYS)
      );
    } else {
      setData(filterGroupsDisabledData(dataItems, [ '' ]));
    }
  }, [ shouldHidePersonalData, dataItems ]);

  useEffect(() => {
    if (!isDisabledSelected) {
      // Нормализация key → dataKey только для Метрик: фикс старых отчётов с позиционными ключами
      if (title === 'Метрики') {
        setSelected(defSelected.map((item: any) => ({ ...item, key: item.dataKey || item.key })));
      } else {
        setSelected(defSelected);
      }
    }
  }, [ defSelected ]);

  useEffect(() => {
    // Устанавливаем checkedKeys из defChecked когда модальное окно закрыто
    // или когда defChecked изменился (например, при загрузке сохранённого отчёта)
    if (!isDisabledSelected) {
      setCheckedKeys(defChecked);
    }
  }, [ defChecked, isDisabledSelected ]);


  const removeSelect = useCallback((key: string) => {
    setSelected((prev) => {
      // Ключи теперь стабильны (основаны на dataKey), просто сравниваем по key.
      // Затем нормализуем под правило AD «первым уровнем — реклама».
      const next = enforceAdFirst([ ...prev ].filter((item) => item.key !== key));
      setCheckedKeys(next.map((item) => item.key ?? '').filter(Boolean));
      return next;
    });
  }, [ enforceAdFirst ]);

  const onExpand = useCallback((keys: React.Key[]) => {
    setExpandedKeys(keys);
    setAutoExpandParent(false);
  }, []);

  const onCheck = useCallback((_checkedKeys: React.Key[] | { checked: React.Key[]; halfChecked: React.Key[] }, e: { checked: boolean; node: TreeNodeData; nativeEvent: MouseEvent }) => {
    const target = e.nativeEvent.target as HTMLElement;
    if (
      target?.nodeName === 'svg' ||
      target?.innerText === 'Да' ||
      target?.innerText === 'Нет' ||
      target?.className === 'settings' ||
      target?.nodeName === 'path'
    ) {
      return;
    }
    
    const nodeKey = e.node.key;
    const nodeDataKey = e.node.dataKey;
    
    // Для Метрик ключи в Tree = dataKey; для остального — item.key (старое поведение)
    const getKey = (item: any) => (title === 'Метрики' ? (item.dataKey || item.key) : item.key);

    // singleSelect: всегда держим ровно один выбранный элемент
    if (singleSelect) {
      if (e.checked) {
        const newSelected: SelectedItem[] = [{ ...(e.node as unknown as SelectedItem) }];
        setSelected(newSelected);
        setCheckedKeys(newSelected.map(getKey).filter((k: any): k is string => k != null));
        return;
      }
      setSelected([]);
      setCheckedKeys([]);
      return;
    }

    setSelected((prev) => {
      // Проверяем, есть ли уже этот элемент в списке (по key или dataKey)
      const existingIndex = prev.findIndex(
        (item) => item.key === nodeKey || item.dataKey === nodeDataKey
      );

      if (existingIndex >= 0) {
        // Элемент уже есть — удаляем его (снимаем галочку).
        // Нормализуем под правило AD «первым уровнем — реклама».
        const newData = [...prev];
        newData.splice(existingIndex, 1);
        const normalized = enforceAdFirst(newData);
        setCheckedKeys(normalized.map(getKey).filter((k: any): k is string => k != null));
        return normalized;
      } else {
        // Элемента нет — добавляем его (ставим галочку)
        const newData = [...prev, { ...e.node } as SelectedItem];
        setCheckedKeys(newData.map(getKey).filter((k: any): k is string => k != null));
        return newData;
      }
    });
  }, [singleSelect, title, enforceAdFirst]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value !== '') {
      const reg = new RegExp(value, 'i');

      const _expandedKeys = listKeys
        .map((item) => {
          if (item.name.search(reg) > -1) {
            return getMetricsParentKey(item.key, dataItems);
          }
          return null;
        })
        .filter(
          (item, i, self) => item && self.indexOf(item) === i
        ) as React.Key[];

      setExpandedKeys(_expandedKeys);
      setSearchValue(value);
      setAutoExpandParent(true);
    } else {
      setExpandedKeys([]);
      setSearchValue('');
      setAutoExpandParent(false);
    }
  }, [listKeys, dataItems]);

  const handleOk = useCallback(() => {
    // AD-отчёт: первым уровнем обязана быть группировка по рекламе. Правило
    // соблюдается при перетаскивании (setList), но обходилось удалением всех
    // рекламных группировок справа — тогда первым оставался не-рекламный уровень.
    // Здесь блокируем применение такого набора.
    if (
      isAdReport &&
      title === 'Группировки' &&
      (collapsedSelected?.length ?? 0) > 0 &&
      !isAdGrouping(collapsedSelected[0])
    ) {
      message.error('Первым уровнем может быть только группировка по рекламе');
      return;
    }
    // «Площадка» вместе с другим срезом Директа — недопустимый набор (возможен только из
    // сохранённого ранее отчёта: в дереве такой выбор заблокирован).
    if (
      isAdReport &&
      title === 'Группировки' &&
      hasAdsDimConflict(collapsedSelected.map((item) => String(item?.dataKey ?? item?.key ?? '')))
    ) {
      message.error('«Площадка» не сочетается с другими срезами Яндекс.Директа. Оставьте что-то одно.');
      return;
    }
    handleSubmit();
    // Применяем схлопнутый порядок: группы регистрации идут единым смежным блоком.
    actionSet(collapsedSelected);
    setIsModalVisible(false);
    setExpandedKeys([]);
  }, [actionSet, collapsedSelected, isAdReport, title, isAdGrouping]);

  // Дерево для рендера: в AD-отчёте, пока не выбрана группировка по рекламе,
  // блокируем остальные узлы (производная от data — не зависит от гонки setData).
  const displayData = useMemo(() => {
    if (isAdReport && title === 'Группировки') {
      const selectedKeys = selected
        .map((item) => String(item?.dataKey ?? item?.key ?? ''))
        .filter(Boolean);
      const tree = selected.some(isAdGrouping) ? data : blockNonAdGroupings(data);
      return applyAdsDimConflicts(tree, selectedKeys);
    }
    return data;
  }, [data, isAdReport, title, selected, isAdGrouping]);

  const handleCancel = useCallback(() => {
    setIsModalVisible(false);
  }, []);

  // Клик по ОТКЛЮЧЁННОМУ чекбоксу среза Директа: сам чекбокс рисует antd вне `titleRender`
  // и по клику ничего не делает, поэтому ловим клик на контейнере дерева и берём причину
  // из подписи той же строки (`data-ads-dim-conflict`).
  const handleTreeContainerClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement | null;
    const checkbox = target?.closest?.('.ant-tree-checkbox-disabled');
    if (!checkbox) return;
    const row = checkbox.closest('.ant-tree-treenode');
    const reason = row?.querySelector<HTMLElement>('[data-ads-dim-conflict]')?.dataset.adsDimConflict;
    if (reason) message.warning(reason);
  }, []);

  // Обработка Enter для применения при открытом модальном окне
  useEffect(() => {
    if (!isModalVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Игнорируем Enter в текстовых полях (input, textarea)
      const target = e.target as HTMLElement;
      const isInputField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
      
      if (e.key === 'Enter' && selected?.length && !isInputField) {
        e.preventDefault();
        handleOk();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalVisible, selected?.length, handleOk]);

  const clearCheckboxes = useCallback(() => {
    setSelected([]);
    setCheckedKeys([]);
    !isDisabledSelected && setisDisabledSelected(true);
    forceUpdate();
  }, [isDisabledSelected, forceUpdate]);

  const confirmCreatedMetricsRemoving = useCallback(async (nodeData: TreeNodeData) => {
    if (nodeData.id == null) return;
    try {
      // Блокируем перезагрузку таблицы при удалении метрики
      setSkipTableFetchUntil(Date.now() + 3000);

      const result = await deleteCustomMetricWithConfirm(nodeData.id, location.pathname, {
        onDeleted: async (deletedIds) => {
          // Убираем из дерева и выбранных ВСЕ удалённые метрики (саму + зависимые)
          const deletedTokens = new Set(deletedIds.map((id) => `_${id}_`));
          setData((prev: TreeNodeData[]) => {
            const updatedData = [ ...prev ];
            const lastItem = updatedData[updatedData.length - 1];
            if (lastItem?.children) {
              lastItem.children = lastItem.children.filter(
                (el) => !deletedTokens.has(el.dataKey)
              );
            }
            return updatedData;
          });
          setSelected((prev) => prev.filter((el) => !deletedTokens.has(el.dataKey)));
          // Убираем удалённые метрики из ВЫБОРА отчёта (Redux state.table.metrics),
          // иначе они остаются в правой колонке и в таблице до перезагрузки
          await purgeDeletedMetricsFromRedux(deletedIds);

          if (selectedSchool?.id) {
            await getCreatedMetrics(selectedSchool.id, location.pathname, undefined, true);
          }
          setEditMetricsState({ id: null, visible: false, type: null });
          showSuccessMessage('Метрика успешно удалена.');
        },
      });

      // Отмена — метрика не удалена, синхронизируем справочник
      if (result === 'cancelled' && selectedSchool?.id) {
        await getCreatedMetrics(selectedSchool.id, location.pathname, undefined, true);
      }
    } catch (err) {
      showErrorMessage('Произошла ошибка во время удаления метрики.');
      console.error(err);
    }
  }, [selectedSchool, getCreatedMetrics, setSkipTableFetchUntil]);

  const handleEditMetric = useCallback((nodeData: TreeNodeData) => {
    setEditMetricsState({ visible: true, id: nodeData.id ?? null, type: null });
  }, []);

  const handleCreateMetricFromExisting = useCallback((nodeData: TreeNodeData) => {
    // Используем as any для обхода ограничения типа в CreateFromExistingMetricModal
    setCreatingMetricState({ visible: true, id: nodeData.id as unknown as null });
  }, []);

  // ==== Обработчики «Групп регистраций» ====
  const handleCreateGroupClick = useCallback(() => {
    setCreateGroupState({ visible: true, initialData: null, duplicate: false });
  }, []);

  const handleEditGroup = useCallback((nodeData: any) => {
    setEditGroupState({ visible: true, id: nodeData?.id ?? null });
  }, []);

  const handleCreateGroupFromExisting = useCallback((nodeData: any) => {
    // Копирование: предзаполняем форму данными исходной группы (name, filter_json, description)
    setCreateGroupState({ visible: true, initialData: nodeData, duplicate: true });
  }, []);

  const confirmGroupRemoving = useCallback(async (nodeData: any) => {
    if (nodeData?.id == null) return;
    const removedDataKey: string = nodeData.dataKey || `custom_group_${nodeData.id}`;
    try {
      // Удаление с проверкой зависимостей: если группа используется осью в отчётах —
      // хелпер покажет подтверждение и удалит каскадно (?confirm=1)
      const result = await deleteCustomGroupWithConfirm(nodeData.id, window.location.pathname, {
        onDeleted: async () => {
          // Явно убираем удалённую группу из выбранных (в окне и в применённых группировках),
          // чтобы «мёртвая» ось не осталась. Список групп в дереве обновит рефетч ниже.
          setSelected((prev) => prev.filter((el) => el?.dataKey !== removedDataKey));
          setCheckedKeys((prev) => prev.filter((k) => k !== removedDataKey));
          setGroupingsAction({ data: (groupings || []).filter((g: any) => g?.dataKey !== removedDataKey) });

          if (selectedSchool?.id) {
            await getCustomGroups(selectedSchool.id, window.location.pathname, undefined, true);
          }
          showSuccessMessage('Группа успешно удалена.');
        },
      });

      // Реальный текст ошибки удаления показывает deleteCustomGroupWithConfirm
      // (getApiErrorMessage) — здесь дубль-тост не выводим, чтобы не было двух окон.
      if (result === 'error') return;
    } catch (err) {
      // Ошибка вне запроса удаления (напр. onDeleted/рефетч) — общий fallback.
      showErrorMessage('Произошла ошибка во время удаления группы.');
      console.error(err);
    }
  }, [ selectedSchool, getCustomGroups, groupings, setGroupingsAction ]);

  // Синхронизируем дерево (data) при изменении dataItems (например, после создания кастомной метрики)
  useEffect(() => {
    if (shouldHidePersonalData) {
      setData(filterGroupsDisabledData(dataItems, PERSONAL_DATA_KEYS));
    } else {
      setData(dataItems);
    }
  }, [ dataItems, shouldHidePersonalData ]);

  useEffect(() => {
    try {
      // Этот useEffect обновляет selected при изменении dataItems (например, при загрузке новых данных)
      // Работает только когда модальное окно открыто (используем ref чтобы не вызывать лишних срабатываний)
      if (!isModalVisibleRef.current) return;
      
      if (selected.length > 1) {
        let newSelected = selected
          .map((el) => {
            // Если есть id, пытаемся найти актуальный объект в dataItems
            if (el.id != null) {
              const found = findObjectById(dataItems, el.id);
              // Если не найден — оставляем оригинальный элемент
              return found || el;
            }
            return el;
          })
          .filter(Boolean) as SelectedItem[];

        // Нормализация key → dataKey только для Метрик
        if (title === 'Метрики') {
          newSelected = newSelected.map((el: any) => el?.dataKey ? { ...el, key: el.dataKey } : el);
        }

        setSelected(newSelected);

        // Используем dataKey для формирования ключей, так как ключи в дереве = dataKey
        const keys = newSelected.map((el) => el.dataKey).filter((k): k is string => k != null);
        setCheckedKeys(keys);
      }
    } catch (err) {
      console.error(err);
    }
  }, [ dataItems ]);
  useEffect(() => {
    if (isModalVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      isDisabledSelected && setSelected(defSelected);
      setisDisabledSelected(false);
      document.body.style.overflow = 'auto scroll';
    }

    return () => {
      document.body.style.overflow = 'auto scroll';
    };
  }, [ isModalVisible ]);

  const listHeight = useMemo(
    () => getLineHeightForModalSettings(selected?.length || 0),
    [ selected?.length ]
  );


const handleSubmit = async () => {
    if (!selectedSchool) {
        console.log('Школа не выбрана');
        return;
    }
    const selectedMetric = metrics.find((metric) => metric.visual_parameters);

    if (!selectedMetric) {
        return;
    }

    try {
        const visualParamsString = selectedMetric.visual_parameters;
        let visualParamsObject = {};
        if (visualParamsString && visualParamsString !== "undefined") {
            try {
                visualParamsObject = JSON.parse(visualParamsString);
            } catch (parseError) {
                console.error("Ошибка при парсинге visual_parameters:", parseError);
            }
        } 
        const values = {
            id: selectedMetric.id, 
            custom_metric_name: selectedMetric.custom_metric_name,  
            visual_parameters: visualParamsObject,  
            school_id: selectedSchool.id,  
            metric_type_id: isConverse ? 2 : 1,  
            description: selectedMetric.description || "",  
            base_metric_name: selectedMetric.base_metric_name,  
            filter_json: isConverse ? null : JSON.stringify(filters),  
            metric_formula: isConverse ? selectedMetric.metric_formula : null,  
            constant_argument: isConverse ? selectedMetric.constant_argument : null,  
        };
        const pathName = location.pathname.includes('dashboard')
            ? getCookie('dashboardPathname')!
            : window.location.pathname;
        const response = await unApi.editCustomMetric(values, pathName);

        if (response.status === 200) {
            console.log('Метрика успешно обновлена');
        } else {
            console.log('Ошибка при обновлении метрики');
        }
    } catch (err) {
        console.error('Ошибка запроса:', err);
    }
};


const lazyExtraNames = [
  'Доп. поля регистрации',
  'Доп. поля пользователя',
  'Доп. поля заказа',
  'Доп. поля заявки',
];


const normalizeTreeData = useCallback((nodes: TreeNodeData[]): TreeNodeData[] =>
  nodes.map((node) => {
    const children = Array.isArray(node.children)
      ? normalizeTreeData(node.children)
      : node.children;

    const hasChildren = Array.isArray(children) && children.length > 0;

    const isLazyNode = lazyExtraNames.includes(String(node.title || node.name));

    return {
      ...node,
      children,
      isLeaf: isLazyNode ? false : !hasChildren,
    };
  }), []);


  return (
    <>
      {!hideTriggerButton && (
        <Button
          data-help-id={title === 'Метрики' ? 'modal-metrics-open' : 'modal-groupings-open'}
          type="default"
          className="report-action-btn"
          disabled={isDisable}
          onClick={() => setIsModalVisible(true)}
        >
          {title}
        </Button>
      )}

      <Modal
        title={title}
        open={isModalVisible}
        onOk={handleOk}
        okText="Применить"
        onCancel={handleCancel}
        cancelText="Отмена"
        centered
        width={modalWidth}
        className="modal-settings"
        destroyOnClose={!simplified}
        okButtonProps={{ disabled: selected?.length ? false : true }}
      >
        <Row gutter={12}>
          <Col span={13}>
            <Input
              style={{ marginBottom: 8 }}
              placeholder={'Поиск ' + title.toLowerCase()}
              onChange={onChange}
              allowClear
            />
            <div style={{ height: listHeight, overflow: 'auto' }} onClick={handleTreeContainerClick}>
              {/* Debug */}
              <div style={{ display: 'none' }}>{JSON.stringify({ checkedKeys, defChecked })}</div>
              <Tree.DirectoryTree
                key={`tree-${title}-${defChecked.length}-${defChecked.slice(0,3).join('-')}`}
                onExpand={onExpand}
                expandedKeys={expandedKeys}
                autoExpandParent={autoExpandParent}
                onCheck={onCheck}
                checkedKeys={checkedKeys}
                blockNode
                checkable
                switcherIcon={<DownOutlined />}
                showIcon={false}
                selectable={false}
                loadData={enableLazyLoading ? onLoadData : undefined}
                treeData={enableLazyLoading ? normalizeTreeData(loopSearch(displayData, searchValue)) : loopSearch(displayData, searchValue)}
                titleRender={(nodeData) => {
                  const node = nodeData as TreeNodeData;

                  // Узел-контейнер «Группы регистраций»: показываем название + иконку «+» для создания группы
                  if ((node as any).isGroupsNode) {
                    return (
                      <span
                        className="group-item-icon-wrapper"
                        style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '10px' }}
                      >
                        <span>
                          {node.title}
                          {node.description && (
                            <span style={{ marginRight: 10, marginLeft: 5 }}>
                              <Tooltip
                                placement="bottom"
                                title={<div className="page-title-tooltip-text">{node.description}</div>}
                                color="#fff"
                                zIndex={9999}
                                overlayClassName="page-title-tooltip"
                              >
                                <QuestionCircleOutlined style={{ color: '#ccc', fontSize: 16 }} />
                              </Tooltip>
                            </span>
                          )}
                        </span>
                        <Tooltip title="Создать группу" placement="top">
                          <PlusOutlined
                            data-help-id="modal-create-group"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleCreateGroupClick();
                            }}
                            style={{ color: '#067F3B', fontSize: 16, marginRight: '5px' }}
                          />
                        </Tooltip>
                      </span>
                    );
                  }

                  // Пункт-переключатель «Группы регистраций» (весь набор одним свёрнутым
                  // уровнем) и контейнер «Выбор групп регистраций» — простой лейбл с тултипом,
                  // без иконок CRUD и без «+».
                  if ((node as any).isGroupSetToggle || (node as any).isGroupsSelectNode) {
                    return (
                      <span
                        className="group-item-icon-wrapper"
                        style={{ display: 'flex', color: 'black', paddingLeft: '10px' }}
                      >
                        <span>
                          {node.title}
                          {node.description && (
                            <span style={{ marginRight: 10, marginLeft: 5 }}>
                              <Tooltip
                                placement="bottom"
                                title={<div className="page-title-tooltip-text">{node.description}</div>}
                                color="#fff"
                                zIndex={9999}
                                overlayClassName="page-title-tooltip"
                              >
                                <QuestionCircleOutlined style={{ color: '#ccc', fontSize: 16 }} />
                              </Tooltip>
                            </span>
                          )}
                        </span>
                      </span>
                    );
                  }

                  // Лист-группа custom_group_<id>: иконки копирования/редактирования/удаления.
                  // Переключатель набора (custom_group_set) сюда не попадает — обработан выше.
                  if (!simplified && typeof (node as any).dataKey === 'string' && (node as any).dataKey.startsWith('custom_group_') && (node as any).dataKey !== 'custom_group_set') {
                    return (
                      <GroupItem
                        nodeData={node}
                        handleEditGroup={handleEditGroup}
                        handleCreateGroupFromExisting={handleCreateGroupFromExisting}
                        confirmGroupRemoving={confirmGroupRemoving}
                      />
                    );
                  }

                  // Для AD отчёта: специальный тултип для заблокированных группировок
                  // Ключи = dataKey; правило то же, что у isAdGrouping (включая параметры Яндекс.Директ)
                  const hasAdGroupingChecked = checkedKeys.some((key) => isAdGroupingKey(String(key)));
                  if (isAdReport && title === 'Группировки' && !hasAdGroupingChecked) {
                    if (isAd.current === 2 && node.disabled === true) {
                      return (
                        <Tooltip
                          placement="top"
                          title={
                            <div className="page-title-tooltip-text">
                              Эти группировки можно использовать только с
                              выбранными группировками по рекламе.
                            </div>
                          }
                          color="#fff"
                          zIndex={9999}
                          overlayClassName="page-title-tooltip"
                        >
                          {node.title}
                        </Tooltip>
                      );
                    }
                  }
                  
                  // Срез Директа, недоступный из-за «Площадки» (или наоборот): подпись не серая,
                  // при наведении — причина, по клику — то же сообщение (чекбокс отключён,
                  // и сам antd Tree по клику ничего не делает).
                  if (node.adsDimConflict) {
                    const conflictMessage = node.adsDimConflict;
                    return (
                      <Tooltip
                        placement="top"
                        title={<div className="page-title-tooltip-text">{conflictMessage}</div>}
                        color="#fff"
                        zIndex={9999}
                        overlayClassName="page-title-tooltip"
                      >
                        <span
                          className="group-item-icon-wrapper"
                          style={{ cursor: 'not-allowed' }}
                          data-ads-dim-conflict={conflictMessage}
                          onClick={(e) => {
                            e.stopPropagation();
                            message.warning(conflictMessage);
                          }}
                        >
                          {node.title}
                          {node.description && (
                            <span style={{ marginRight: 10, marginLeft: 5 }}>
                              <Tooltip
                                placement="bottom"
                                title={<div className="page-title-tooltip-text">{node.description}</div>}
                                color="#fff"
                                zIndex={9999}
                                overlayClassName="page-title-tooltip"
                              >
                                <QuestionCircleOutlined style={{ color: '#ccc', fontSize: 16 }} />
                              </Tooltip>
                            </span>
                          )}
                        </span>
                      </Tooltip>
                    );
                  }

                  return node.disabled ? (
                    <Tooltip
                      placement="top"
                      title={
                        title === 'Группировки' ? (
                          <div className="page-title-tooltip-text">
                            {isAdReport
                              ? 'Эти группировки можно использовать только с выбранными группировками по рекламе.'
                              : 'Эти группировки можно использовать только с выбранными группировками по рекламе.'}
                          </div>
                        ) : (
                          <div className="page-title-tooltip-text">
                            Эти метрику нельзя использовать с выбранными
                            группировками.
                          </div>
                        )
                      }
                      color="#fff"
                      zIndex={9999}
                      overlayClassName="page-title-tooltip"
                    >
                      {node.title}
                    </Tooltip>
                  ) : (
                    <>
                      {node.description ? (
                        <>
                          {node.disableCheckbox ? (
                            <Tooltip
                              placement="top"
                              title={
                                <div className="page-title-tooltip-text">
                                  Контактные данные пользователей не доступны.
                                  Обратитесь к администратору, чтобы получить к
                                  ним доступ.
                                </div>
                              }
                              color="#fff"
                              zIndex={9999}
                              overlayClassName="page-title-tooltip"
                            >
                              <span className="group-item-icon-wrapper">
                                {node.title}
                                <span
                                  style={{ marginRight: 10, marginLeft: 5 }}
                                >
                                  <Tooltip
                                    placement="bottom"
                                    title={
                                      <div
                                        className="page-title-tooltip-text"
                                        dangerouslySetInnerHTML={{
                                          __html: node.description || '',
                                        }}
                                      ></div>
                                    }
                                    color="#fff"
                                    zIndex={9999}
                                    overlayClassName="page-title-tooltip"
                                  >
                                    <QuestionCircleOutlined
                                      style={{ color: '#ccc', fontSize: 16 }}
                                    />
                                  </Tooltip>
                                </span>
                              </span>
                            </Tooltip>
                          ) : (
                            simplified ? (
                              <span className="group-item-icon-wrapper">
                                {node.title}
                                <span style={{ marginRight: 10, marginLeft: 5 }}>
                                  <Tooltip
                                    placement="bottom"
                                    title={
                                      <div
                                        className="page-title-tooltip-text"
                                        dangerouslySetInnerHTML={{
                                          __html: node.description || '',
                                        }}
                                      ></div>
                                    }
                                    color="#fff"
                                    zIndex={9999}
                                    overlayClassName="page-title-tooltip"
                                  >
                                    <QuestionCircleOutlined
                                      style={{ color: '#ccc', fontSize: 16 }}
                                    />
                                  </Tooltip>
                                </span>
                              </span>
                            ) : (
                              <MetricItem
                                handleCreateMetricFromExisting={
                                  handleCreateMetricFromExisting
                                }
                                nodeData={node}
                                handleEditMetric={handleEditMetric}
                                confirmCreatedMetricsRemoving={
                                  confirmCreatedMetricsRemoving
                                }
                              />
                            )
                          )}
                        </>
                      ) : (
                        <>
                          {node.disableCheckbox ? (
                            <Tooltip
                              placement="top"
                              title={
                                <div className="page-title-tooltip-text">
                                  Контактные данные пользователей не доступны.
                                  Обратитесь к администратору, чтобы получить к
                                  ним доступ.
                                </div>
                              }
                              color="#fff"
                              zIndex={9999}
                              overlayClassName="page-title-tooltip"
                            >
                              <span>{node.title}</span>
                            </Tooltip>
                          ) : (
                            simplified ? (
                              <span>{node.title}</span>
                            ) : (
                              <MetricItem
                                handleCreateMetricFromExisting={
                                  handleCreateMetricFromExisting
                                }
                                nodeData={node}
                                handleEditMetric={handleEditMetric}
                                confirmCreatedMetricsRemoving={
                                  confirmCreatedMetricsRemoving
                                }
                              />
                            )
                          )}
                        </>
                      )}
                    </>
                  );
                }}
              />
            </div>
            <Space style={{ marginTop: '20px' }}>
              {title === 'Метрики' && enableCustomMetrics && !simplified && (
                <Button
                  data-help-id="modal-create-metric"
                  type="primary"
                  icon={<ClearOutlined />}
                  onClick={() => setIsCreateMetricsVisible(true)}
                >
                  Создать метрику
                </Button>
              )}
              <div>
                <Button
                  data-help-id="modal-clear-selection"
                  type="default"
                  icon={<ClearOutlined />}
                  onClick={() => clearCheckboxes()}
                >
                  Очистить выбор
                </Button>

              </div>
            </Space>
          </Col>
          <Col span={11}>
            <ReactSortable
              list={collapsedSelected?.map((item) => ({ ...item, id: item.id ?? item.key ?? '', chosen: true })) ?? []}
              className="modalSortable__container"
              style={collapsedSelected?.length ? {} : {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%' }}
              onUpdate={(evt) => {
                // onUpdate вызывается ДО setList (react-sortablejs) — сохраняем индекс сброса
                dragNewIndexRef.current = typeof evt.newIndex === 'number' ? evt.newIndex : -1;
                // Пользователь вручную менял порядок — блокируем ресинк selected из defSelected,
                // чтобы поздняя загрузка справочника групп не перетёрла порядок обратно
                if (!isDisabledSelected) setisDisabledSelected(true);
              }}
              setList={(newList) => {
                // Для AD отчёта: проверяем, что первым элементом остаётся рекламная группировка
                if (isAdReport && title === 'Группировки' && newList.length > 0) {
                  const firstItem = newList[0] as SelectedItem;
                  // Проверяем, что первый элемент — рекламная группировка
                  if (!isAdGrouping(firstItem)) {
                    // Находим рекламную группировку в списке
                    const adIndex = newList.findIndex((item) => isAdGrouping(item as SelectedItem));
                    if (adIndex > 0) {
                      // Перемещаем рекламную группировку на первое место
                      const adItem = newList.splice(adIndex, 1)[0];
                      newList.unshift(adItem);
                      message.error('Первым уровнем может быть только группировка по рекламе');
                    }
                  }
                }
                // Группы регистрации — единым смежным блоком; при перетаскивании ГРУППЫ
                // блок встаёт на место сброса (можно двигать блок и менять порядок групп).
                const dropIndex = dragNewIndexRef.current;
                dragNewIndexRef.current = -1;
                setSelected(collapseGroupsAfterDrag(newList as SelectedItem[], dropIndex));
              }}
              animation={200}
              delay={2}
            >
              {collapsedSelected?.length ? collapsedSelected.map((item, idx) => {
                // Обычная ось группировки — прежний рендер.
                if (!isRegistrationGroupItem(item)) {
                  return (
                    <div className="drag-item" key={item.key}>
                      <span className="drag-handler">
                        <EllipsisOutlined rotate={90} />
                        <span
                          className="drag-label"
                          style={{
                            backgroundColor: item?.background
                              ? item.background
                              : 'unset',
                            width: '100%',
                          }}
                        >
                          {item?.label || item.name}
                        </span>
                      </span>
                      <div
                        onClick={() => removeSelect(item.key ?? '')}
                        className="drag-remove"
                      >
                        <CloseOutlined />
                      </div>
                    </div>
                  );
                }

                // Группа регистрации: рендерим как часть блока «Группы регистраций».
                const isFirstGroup = !isRegistrationGroupItem(collapsedSelected[idx - 1]);
                const isLastGroup = !isRegistrationGroupItem(collapsedSelected[idx + 1]);
                const groupClasses = [ 'drag-item', 'drag-item--group' ];
                if (isFirstGroup) groupClasses.push('drag-item--group-first');
                if (isLastGroup) groupClasses.push('drag-item--group-last');

                return (
                  <div className={groupClasses.join(' ')} key={item.key}>
                    {isFirstGroup && (
                      <div className="drag-group-title">{REGISTRATION_GROUPS_NODE_NAME}</div>
                    )}
                    <div className="drag-group-body">
                      <span className="drag-handler">
                        <EllipsisOutlined rotate={90} />
                        <span className="drag-label drag-label--nested" style={{ width: '100%' }}>
                          {item?.label || item.name}
                        </span>
                      </span>
                      <div
                        onClick={() => removeSelect(item.key ?? '')}
                        className="drag-remove"
                      >
                        <CloseOutlined />
                      </div>
                    </div>
                  </div>
                );
              }) :
                  <span style={{ fontSize: 'large' }}>
                    {`Выберите хотя бы 1 ${title === 'Метрики' ? 'метрику' : 'группировку'}`}
                  </span>
              }
            </ReactSortable>
          </Col>
        </Row>

        {!simplified && editMetricsState.visible && (
          <EditMetricsModal
            confirmCreatedMetricsRemoving={confirmCreatedMetricsRemoving}
            id={editMetricsState.id}
            visible={editMetricsState.visible}
            setEditState={setEditMetricsState}
          />
        )}
        {!simplified && creatingMetricState.visible && (
          <CreateFromExistingMetricModal
            id={creatingMetricState.id}
            visible={creatingMetricState.visible}
            setCreateState={setCreatingMetricState}
          />
        )}
        {!simplified && createGroupState.visible && (
          <CreateGroupModal
            visible={createGroupState.visible}
            initialData={createGroupState.initialData}
            duplicate={createGroupState.duplicate}
            onClose={() => setCreateGroupState({ visible: false, initialData: null, duplicate: false })}
          />
        )}
        {!simplified && editGroupState.visible && (
          <EditGroupModal
            visible={editGroupState.visible}
            id={editGroupState.id}
            onClose={() => setEditGroupState({ visible: false, id: null })}
          />
        )}
      </Modal>
    </>
  );
};

export default ModalSettings;
